import { useEffect, useRef, useState } from "react";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { ContentView } from "../../components/content-view/ContentView";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import { actionItems } from "./PollingFrequencyAdminActions";
import { usePollingFrequencyData } from "../../queries/polling-frequency-admin/UsePollingFrequencyData";
import { PollingFrequencyAdminTabEnum, usePollingFrequencyAdminState } from "./PollingFrequencyAdminState";
import TabBar from "../../components/tab-bar/TabBar";
import { useForm, useWatch } from "react-hook-form";
import PollingFrequencyUtils, { PollingFrequencyForm, defaultPollingFrequencyForm, pollingScheduleOptions } from "./PollingFrequencyUtils";
import Form from "../../components/forms/form/Form";
import TabWithCount from "../../components/tab-with-count/TabWithCount";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import TabContainer from "../../components/tab-container/TabContainer";
import PollingFrequencyObjectList, { PollingFrequencyOptionListType } from "./polling-frequency-object-list/PollingFrequencyObjectList";
import { useTranslation } from "react-i18next";
import { ContentSection } from "../../components/content-section/ContentSection";
import OptionsSideSheet from "../../components/options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import { ContentContainer } from "../../components/content-container/ContentContainer";
import SideSheetSwitch from "../../components/side-sheet/side-sheet-switch/SideSheetSwitch";
import FormPropertySection from "../../components/forms/form-property-section/FormPropertySection";
import FormSelect from "../../components/forms/form-select/FormSelect";
import { Box, Divider, Fab, MenuItem, useTheme } from "@mui/material";
import FormProperty from "../../components/forms/form-property/FormProperty";
import { Save } from "@mui/icons-material";
import { useSavePollingFrequencyData } from "../../queries/polling-frequency-admin/UseSavePollingFrequencyData";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { IPollingScheduleDataModel, PollingScheduleEnum } from "../../services/polling-frequency-data-service/Models";
import NoData from "../../components/no-data/NoData";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";

export default function PollingFrequencyAdminView() {

    const { t } = useTranslation();
    const { confirm
        , acknowledge 
    } = useConfirmation();
    const [userState] = useUserContext();    
    const routeContext = useRouteContext();
    const [viewOptionState] = useState(ViewOptionStateEnum.Default);
    const [groupID, setGroupID] = useState<number>(routeContext.groupID as number);
    const theme = useTheme();

    let deviceListRef = useRef<PollingFrequencyOptionListType>(undefined);
    let tankListRef = useRef<PollingFrequencyOptionListType>(undefined);
    let tatListRef = useRef<PollingFrequencyOptionListType>(undefined);

    const {
        state, 
        setActiveTab,
        setViewSelected,
        setDeviceFilter,
        setTankFilter,
        setTatFilter,
        setUpdating,
        resetState
    } = usePollingFrequencyAdminState();


    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
    });


    const {data, status, refetch } = useQueryNotifications(usePollingFrequencyData)({
        contextGroupID: groupID,
        onSuccess: (returnData) => {

            if (returnData) {
                const form = PollingFrequencyUtils.mapToFormValues(returnData);
                reset(form);
            }
        }
    });

    const { mutate: savePollingSchedules, status: saveStatus } = useQueryNotifications(useSavePollingFrequencyData)({
        groupID: groupID,
        onSuccess: async () => {
            acknowledge(t("pollingFrequencyAdmin.dataSavedMessage"));

            setActiveTab(PollingFrequencyAdminTabEnum.Devices);

            await refetch();

            setUpdating(false);
        },
        onError: () => {
            setUpdating(false);
        }
    });

    const form = useForm<PollingFrequencyForm>({
        mode: 'onChange',
        defaultValues: {...defaultPollingFrequencyForm
        }
    });

    const {
        getValues,
        setValue,
        watch,
        formState: {
            //dirtyFields,
            isDirty,
            //isValid,
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;

    const [
        watchDevices,
        watchTanks,
        watchTats,
        watchNewSchedule
    ] = watch([
        'devices',
        'tanks',
        'tats',
        'newSchedule'
    ]);

    const pollingFrequencyFormData = useWatch({ control: control }) as PollingFrequencyForm;

    const deviceCount = pollingFrequencyFormData.devices.length;
    const tankCount = pollingFrequencyFormData.tanks.length;
    const tatCount = pollingFrequencyFormData.tats.length;
    const availableObjectCount = deviceCount + tankCount;


    const selectedDeviceCount = watchDevices.filter(o => o.selected).length;
    const selectedTankCount = watchTanks.filter(o => o.selected).length;
    const selectedTatCount = watchTats.filter(o => o.selected).length;

    const hasSelectedObjects = selectedDeviceCount > 0 ||
                selectedTankCount > 0;

    const enableViewSelected = state.activeTab === PollingFrequencyAdminTabEnum.Devices ?
            selectedDeviceCount > 0 :
        state.activeTab === PollingFrequencyAdminTabEnum.Tanks ?
            selectedTankCount > 0 :
            selectedTatCount > 0;

    const filterLabel = state.activeTab === PollingFrequencyAdminTabEnum.Devices ?
                        getObjectTypeName(eLynxObjectTypeEnum.Device) :
                        state.activeTab === PollingFrequencyAdminTabEnum.Tanks ?
                        getObjectTypeName(eLynxObjectTypeEnum.Device) :
                        t('pollingFrequencyAdmin.tagType');

    const filterText = state.activeTab === PollingFrequencyAdminTabEnum.Devices ?
                        state.deviceNameFilter :
                        state.activeTab === PollingFrequencyAdminTabEnum.Tanks ?
                        state.tankNameFilter :
                        state.tatNameFilter;

    const enableSave = isDirty && hasSelectedObjects && watchNewSchedule !== -1;

    function onExecuteAction(menuItem: ActionMenuItem) {

        const action = menuItem.action as BaseActionEnum;

        if (action === BaseActionEnum.Refresh) {
            refetch();
        } else {
            if (state.activeTab === PollingFrequencyAdminTabEnum.Devices) {

                deviceListRef.current?.executeAction(action);

            } else if (state.activeTab === PollingFrequencyAdminTabEnum.Tanks) {

                tankListRef.current?.executeAction(action);

            } else {

                tatListRef.current?.executeAction(action);

            }
        }
    }

    function onGroupChanged(item: INavigationItem) {
        setGroupID(item.objectID);
    }

    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }

    function onSetFilter(value: string) {
        
        if (state.activeTab === PollingFrequencyAdminTabEnum.Devices) {
            setDeviceFilter(value);
        } else if (state.activeTab === PollingFrequencyAdminTabEnum.Tanks) {
            setTankFilter(value);
        } else {
            setTatFilter(value);
        }
    }

    async function onSubmitFormData (event: React.MouseEvent<HTMLButtonElement>,
        values: PollingFrequencyForm) {

        event.preventDefault();

        const pollingFrequencyData = PollingFrequencyUtils.mapToModel(data as IPollingScheduleDataModel, values);

        if (pollingFrequencyData.some(o => o.pollingSchedule === PollingScheduleEnum.OneMinute ||
            o.pollingSchedule === PollingScheduleEnum.FiveMinute)) {
                const result = await confirm({
                    dialogTitle: t("pollingFrequencyAdmin.highFrequencyWarning.title"),
                    confirmationText: t("pollingFrequencyAdmin.highFrequencyWarning.text"),
                    yesButtonText: t("buttons.yesContinue"),
                    noButtonText: t("buttons.noCancel"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: false
                });
                
            if (result !== "yes"){
                return;
            }
        }

        setUpdating(true);

        savePollingSchedules(pollingFrequencyData);
    }

    useEffect(() => {
        if (state.viewSelected) {

            if (state.activeTab === PollingFrequencyAdminTabEnum.Devices && selectedDeviceCount === 0) {
                setViewSelected(false);
            } else if (state.activeTab === PollingFrequencyAdminTabEnum.Tanks && selectedTankCount === 0) {
                setViewSelected(false);
            } else if (state.activeTab === PollingFrequencyAdminTabEnum.Tats && selectedTatCount === 0) {
                setViewSelected(false);
            }
        }
    }, [
        state.viewSelected,
        state.activeTab,
        setViewSelected,
        selectedDeviceCount,
        selectedTankCount,
        selectedTatCount
    ]);

    useEffect(() => {
        if (!hasSelectedObjects && getValues('newSchedule') !== -1) {

            setValue('newSchedule', -1, {shouldTouch: true});

        }
    }, [
        hasSelectedObjects,
        getValues,
        setValue
    ])


    return (
        <ContentView>
            <ContextualAppBar 
                hideBreadcrumb={true}
                actions={actionItems} 
                viewOptionState={viewOptionState}
                onExecuteAction={onExecuteAction} 
                showGroupPicker={true}
                onGroupSelected={onGroupChanged}
                groupID={groupID}
            />

                <LoadingZone isLoading={status === 'loading' || saveStatus === 'loading' || state.updating}>

                {data && availableObjectCount > 0 && 

                <ContentContainer>
                    <ContentSection>

                <Form form={form} sx={{ padding: 0 }}>

                    <TabBar value={state.activeTab}
                            onChange={(_event, value) => setActiveTab(value)}>

                            {deviceCount > 0 ? 
                                <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Device, true)} 
                                    count={selectedDeviceCount}
                                    value={PollingFrequencyAdminTabEnum.Devices}
                                /> : null
                            }

                            {tankCount > 0 ? 
                                <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Tank, true)} 
                                    count={selectedTankCount}
                                    value={PollingFrequencyAdminTabEnum.Tanks}
                                /> : null
                            }

                            {tatCount > 0 ? 
                                <TabWithCount label={t('pollingFrequencyAdmin.tagTypes')} 
                                    count={selectedTatCount}
                                    value={PollingFrequencyAdminTabEnum.Tats}
                                /> : null
                            }

                            

                        </TabBar>


                    {deviceCount > 0 ? <TabContainer visible={state.activeTab === PollingFrequencyAdminTabEnum.Devices}>
                        <PollingFrequencyObjectList 
                            ref={deviceListRef}
                            objectType={eLynxObjectTypeEnum.Device} 
                            viewSelected={state.viewSelected} 
                            filterText={state.deviceNameFilter} 
                        />
                    </TabContainer> : null
                    }

                    {tankCount > 0 ? <TabContainer visible={state.activeTab === PollingFrequencyAdminTabEnum.Tanks}>
                        <PollingFrequencyObjectList 
                            ref={tankListRef}
                            objectType={eLynxObjectTypeEnum.Tank} 
                            viewSelected={state.viewSelected} 
                            filterText={state.tankNameFilter} 
                        />
                    </TabContainer> : null
                    }

                    {tatCount > 0 ? <TabContainer visible={state.activeTab === PollingFrequencyAdminTabEnum.Tats}>
                        <PollingFrequencyObjectList 
                            ref={tatListRef}
                            objectType={eLynxObjectTypeEnum.Tag} 
                            viewSelected={state.viewSelected} 
                            filterText={state.tatNameFilter} 
                        />
                    </TabContainer> : null
                    }


                    <Divider sx={{
                            //marginTop: 2,
                            marginBottom: 2
                    }}/>

                    <FormPropertySection label='Schedule' sx={{marginLeft: 1}}>
                        <FormProperty>

                            <FormSelect
                                id="newSchedule"
                                name="newSchedule"
                                label={t('pollingFrequencyAdmin.newSchedule')}
                                disabled={!hasSelectedObjects}
                                sx={{ 
                                    width: 1,
                                    marginTop: 1,
                                    marginBottom: 2,
                                    }}>

                                {pollingScheduleOptions.map((o, index) => 
                                    <MenuItem key={index} 
                                        value={o.Key}
                                        disabled={o.Key === -1}
                                        >
                                        {o.Value}
                                    </MenuItem>
                                )}

                            </FormSelect>
                        </FormProperty>
                    </FormPropertySection>



                    <Fab
                        color="secondary"
                        id="submit"
                        type="submit"
                        name="update"
                        disabled={!enableSave}
                        aria-label="save form data"
                        sx={{
                            position: "absolute",
                            bottom: theme.spacing(2),
                            right: theme.spacing(2)
                        }}
                        onClick={(e) => handleSubmit((data) => onSubmitFormData(e, data))()}>
                        <Save />
                    </Fab>

                </Form>

                </ContentSection>

                <OptionsSideSheet onReset={resetState}>

                <SideSheetSwitch
                    label={t("pollingFrequencyAdmin.viewSelected", {name: `${filterLabel}s`})}
                            checked={state.viewSelected}
                            onChange={(checked: boolean) => {
                                onViewSelected(checked);
                            }}
                            disabled={!enableViewSelected} />

                    
                    <SideSheetFilterInput
                        label={t("pollingFrequencyAdmin.filterBy", {name: filterLabel})}
                        value={filterText}
                        onChange={(value: string) => {
                            onSetFilter(value);
                        }}
                    />


                </OptionsSideSheet>

                </ContentContainer>

                }

                {data && availableObjectCount === 0 &&
                 
                    <Box sx={{
                        padding: 1
                    }}>
                        <NoData
                            title={t('pollingFrequencyAdmin.nodata.title', {name: group?.title})}
                            text={t('pollingFrequencyAdmin.nodata.text')} />
                    </Box>
                }

                </LoadingZone>                
        </ContentView>
    );
  }
